// Offline write queue. Server-bound mutations get enqueued when offline
// (or always, if `enqueueAlways` is set) and flushed by the sync engine.
import { offlineDB, type OutboxItem, type OutboxOp } from "./db";
import { setPendingCount } from "./connection";

export interface EnqueueArgs {
  op: OutboxOp;
  table: string;
  payload: unknown;
  match?: Record<string, unknown>;
  dedupeKey?: string;
  user_id?: string;
  guard?: OutboxItem["guard"];
}

export async function enqueue(args: EnqueueArgs): Promise<number | null> {
  const db = offlineDB();
  if (!db) return null;
  const item: OutboxItem = {
    op: args.op,
    table: args.table,
    payload: args.payload,
    match: args.match,
    dedupeKey: args.dedupeKey,
    guard: args.guard,
    user_id: args.user_id,
    created_at: Date.now(),
    attempts: 0,
    status: "pending",
  };
  // Collapse duplicates by dedupeKey: replace the prior pending row.
  if (args.dedupeKey) {
    const prior = await db.outbox.where("dedupeKey").equals(args.dedupeKey).first();
    if (prior?.id != null) await db.outbox.delete(prior.id);
  }
  const id = await db.outbox.add(item);
  await refreshPendingCount();
  return id as number;
}

export async function refreshPendingCount() {
  const db = offlineDB();
  if (!db) return;
  const n = await db.outbox.where("status").notEqual("syncing").count();
  setPendingCount(n);
}

export async function listOutbox(): Promise<OutboxItem[]> {
  const db = offlineDB();
  if (!db) return [];
  return db.outbox.orderBy("created_at").toArray();
}

export async function removeOutboxItem(id: number) {
  const db = offlineDB();
  if (!db) return;
  await db.outbox.delete(id);
  await refreshPendingCount();
}
